/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import{addNearFar as e,addLinearDepth as o}from"../views/3d/webgl-engine/core/shaderLibrary/ForwardLinearDepth.glsl.js";import{ShaderOutput as t}from"../views/3d/webgl-engine/core/shaderLibrary/ShaderOutput.js";import{SliceDraw as r}from"../views/3d/webgl-engine/core/shaderLibrary/Slice.glsl.js";import{Transform as a}from"../views/3d/webgl-engine/core/shaderLibrary/Transform.glsl.js";import{VertexColor as i}from"../views/3d/webgl-engine/core/shaderLibrary/attributes/VertexColor.glsl.js";import{OutputDepth as l}from"../views/3d/webgl-engine/core/shaderLibrary/output/OutputDepth.glsl.js";import{OutputHighlight as n}from"../views/3d/webgl-engine/core/shaderLibrary/output/OutputHighlight.glsl.js";import{multipassTerrainTest as c}from"../views/3d/webgl-engine/core/shaderLibrary/shading/MultipassTerrainTest.glsl.js";import{VisualVariables as d}from"../views/3d/webgl-engine/core/shaderLibrary/shading/VisualVariables.glsl.js";import{symbolAlphaCutoff as s}from"../views/3d/webgl-engine/core/shaderLibrary/util/AlphaCutoff.js";import{ColorConversion as p}from"../views/3d/webgl-engine/core/shaderLibrary/util/ColorConversion.glsl.js";import{addProjViewLocalOrigin as g,addCameraPosition as v}from"../views/3d/webgl-engine/core/shaderLibrary/util/View.glsl.js";import{Float4PassUniform as u}from"../views/3d/webgl-engine/core/shaderModules/Float4PassUniform.js";import{FloatPassUniform as m}from"../views/3d/webgl-engine/core/shaderModules/FloatPassUniform.js";import{glsl as f}from"../views/3d/webgl-engine/core/shaderModules/interfaces.js";import{ShaderBuilder as h}from"../views/3d/webgl-engine/core/shaderModules/ShaderBuilder.js";import{TransparencyPassType as w}from"../views/3d/webgl-engine/lib/TransparencyPassType.js";import{VertexAttribute as b}from"../views/3d/webgl-engine/lib/VertexAttribute.js";import{Style as y}from"../views/3d/webgl-engine/materials/PatternStyle.js";const S=.70710678118,j=S,C=.08715574274;function x(x){const P=new h,R=x.multipassEnabled&&(x.output===t.Color||x.output===t.Alpha),{vertex:$,fragment:D,attributes:V,varyings:A}=P;g($,x),P.include(a,x),P.include(i,x),P.include(d,x),x.draped?$.uniforms.add(new m("worldToScreenRatio",((e,o)=>1/o.screenToPCSRatio))):V.add(b.BOUNDINGRECT,"mat3"),V.add(b.POSITION,"vec3"),V.add(b.UVMAPSPACE,"vec4"),x.vvColor&&V.add(b.COLORFEATUREATTRIBUTE,"float"),A.add("vColor","vec4"),A.add("vpos","vec3"),A.add("vuv","vec2"),R&&A.add("depth","float"),$.uniforms.add(new u("uColor",(e=>e.color)));const L=x.style===y.ForwardDiagonal||x.style===y.BackwardDiagonal||x.style===y.DiagonalCross;L&&$.code.add(f`
      const mat2 rotate45 = mat2(${f.float(S)}, ${f.float(-j)},
                                 ${f.float(j)}, ${f.float(S)});
    `),x.draped||(v($,x),$.uniforms.add(new m("worldToScreenPerDistanceRatio",((e,o)=>1/o.camera.perScreenPixelRatio))),$.code.add(f`vec3 projectPointToLineSegment(vec3 center, vec3 halfVector, vec3 point) {
float projectedLength = dot(halfVector, point - center) / dot(halfVector, halfVector);
return center + halfVector * clamp(projectedLength, -1.0, 1.0);
}`),$.code.add(f`vec3 intersectRayPlane(vec3 rayDir, vec3 rayOrigin, vec3 planeNormal, vec3 planePoint) {
float d = dot(planeNormal, planePoint);
float t = (d - dot(planeNormal, rayOrigin)) / dot(planeNormal, rayDir);
return rayOrigin + t * rayDir;
}`),$.code.add(f`
      float boundingRectDistanceToCamera() {
        vec3 center = vec3(boundingRect[0][0], boundingRect[0][1], boundingRect[0][2]);
        vec3 halfU = vec3(boundingRect[1][0], boundingRect[1][1], boundingRect[1][2]);
        vec3 halfV = vec3(boundingRect[2][0], boundingRect[2][1], boundingRect[2][2]);
        vec3 n = normalize(cross(halfU, halfV));

        vec3 viewDir = - vec3(view[0][2], view[1][2], view[2][2]);

        float viewAngle = dot(viewDir, n);
        float minViewAngle = ${f.float(C)};

        if (abs(viewAngle) < minViewAngle) {
          // view direction is (almost) parallel to plane -> clamp it to min angle
          float normalComponent = sign(viewAngle) * minViewAngle - viewAngle;
          viewDir = normalize(viewDir + normalComponent * n);
        }

        // intersect view direction with infinite plane that contains bounding rect
        vec3 planeProjected = intersectRayPlane(viewDir, cameraPosition, n, center);

        // clip to bounds by projecting to u and v line segments individually
        vec3 uProjected = projectPointToLineSegment(center, halfU, planeProjected);
        vec3 vProjected = projectPointToLineSegment(center, halfV, planeProjected);

        // use to calculate the closest point to camera on bounding rect
        vec3 closestPoint = uProjected + vProjected - center;

        return length(closestPoint - cameraPosition);
      }
    `)),$.code.add(f`
    vec2 scaledUV() {
      vec2 uv = uvMapSpace.xy ${L?" * rotate45":""};
      vec2 uvCellOrigin = uvMapSpace.zw ${L?" * rotate45":""};

      ${x.draped?"":f`
            float distanceToCamera = boundingRectDistanceToCamera();
            float worldToScreenRatio = worldToScreenPerDistanceRatio / distanceToCamera;
          `}

      // Logarithmically discretize ratio to avoid jittering
      float step = 0.1;
      float discreteWorldToScreenRatio = log(worldToScreenRatio);
      discreteWorldToScreenRatio = ceil(discreteWorldToScreenRatio / step) * step;
      discreteWorldToScreenRatio = exp(discreteWorldToScreenRatio);

      vec2 uvOffset = mod(uvCellOrigin * discreteWorldToScreenRatio, ${f.float(x.patternSpacing)});
      return uvOffset + (uv * discreteWorldToScreenRatio);
    }
  `);const O=x.output===t.Depth;return O&&(P.include(l,x),e(P),o(P)),$.code.add(f`
    void main(void) {
      vuv = scaledUV();
      vpos = position;
      ${R?"depth = (view * vec4(vpos, 1.0)).z;":""}
      forwardNormalizedVertexColor();
      ${x.hasVertexColors?"vColor *= uColor;":x.vvColor?"vColor = uColor * interpolateVVColor(colorFeatureAttribute);":"vColor = uColor;"}
      gl_Position = ${O?f`transformPositionWithDepth(proj, view, vpos, nearFar, linearDepth);`:f`transformPosition(proj, view, vpos);`}
    }
  `),P.include(r,x),D.include(p),x.draped&&D.uniforms.add(new m("texelSize",((e,o)=>1/o.camera.pixelRatio))),x.output===t.Highlight&&P.include(n,x),R&&P.include(c,x),x.output!==t.Highlight&&(D.code.add(f`
      const float lineWidth = ${f.float(x.lineWidth)};
      const float spacing = ${f.float(x.patternSpacing)};
      const float spacingINV = ${f.float(1/x.patternSpacing)};

      float coverage(float p, float txlSize) {
        p = mod(p, spacing);

        float halfTxlSize = txlSize / 2.0;

        float start = p - halfTxlSize;
        float end = p + halfTxlSize;

        float coverage = (ceil(end * spacingINV) - floor(start * spacingINV)) * lineWidth;
        coverage -= min(lineWidth, mod(start, spacing));
        coverage -= max(lineWidth - mod(end, spacing), 0.0);

        return coverage / txlSize;
      }
    `),x.draped||D.code.add(f`const int maxSamples = 5;
float sampleAA(float p) {
vec2 dxdy = abs(vec2(dFdx(p), dFdy(p)));
float fwidth = dxdy.x + dxdy.y;
ivec2 samples = 1 + ivec2(clamp(dxdy, 0.0, float(maxSamples - 1)));
vec2 invSamples = 1.0 / vec2(samples);
float accumulator = 0.0;
for (int j = 0; j < maxSamples; j++) {
if(j >= samples.y) {
break;
}
for (int i = 0; i < maxSamples; i++) {
if(i >= samples.x) {
break;
}
vec2 step = vec2(i,j) * invSamples - 0.5;
accumulator += coverage(p + step.x * dxdy.x + step.y * dxdy.y, fwidth);
}
}
accumulator /= float(samples.x * samples.y);
return accumulator;
}`)),D.code.add(f`
    void main() {
      discardBySlice(vpos);
      ${R?"terrainDepthTest(depth);":""}
      vec4 color = vColor;
      color = highlightSlice(color, vpos);

      ${x.output!==t.Highlight?f`color.a *= ${T(x)};`:""}

      ${x.output===t.ObjectAndLayerIdColor?f`color.a = 1.0;`:""}

      if (color.a < ${f.float(s)}) {
        discard;
      }

      ${x.output===t.Alpha?f`fragColor = vec4(color.a);`:""}

      ${x.output===t.Color?f`fragColor = color; ${x.transparencyPassType===w.Color?"fragColor = premultiplyAlpha(fragColor);":""}`:""}
      ${x.output===t.Highlight?f`outputHighlight();`:""}
      ${x.output===t.Depth?f`outputDepth(linearDepth);`:""};
    }
  `),P}function T(e){function o(o){return e.draped?f`coverage(vuv.${o}, texelSize)`:f`sampleAA(vuv.${o})`}switch(e.style){case y.ForwardDiagonal:case y.Horizontal:return o("y");case y.BackwardDiagonal:case y.Vertical:return o("x");case y.DiagonalCross:case y.Cross:return f`
        1.0 - (1.0 - ${o("x")}) * (1.0 - ${o("y")})
      `;default:return"0.0"}}const P=Object.freeze(Object.defineProperty({__proto__:null,build:x},Symbol.toStringTag,{value:"Module"}));export{P,x as b};
